import React, { useEffect, useState } from 'react';
import { Button, Modal } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import qs from 'qs';
import { columnOrderMapReports } from '../../constants/excelData';
import { reportingOp } from 'store/ducks/Reportings';
import { RootState } from 'store/configureStore';
import RT0101 from 'components/Tables/Reporting/B0101/B0101';
import RT0102 from 'components/Tables/Reporting/B0102/B0102';
import RT0103 from 'components/Tables/Reporting/B0103/B0103';
import RT0201 from 'components/Tables/Reporting/B0201/B0201';
import RT0202 from 'components/Tables/Reporting/B0202/B0202';
import RT0203 from 'components/Tables/Reporting/B0203/B0203';
import RT0301 from 'components/Tables/Reporting/B0301/B0301';
import RT0302 from 'components/Tables/Reporting/B0302/B0302';
import RT0303 from 'components/Tables/Reporting/B0303/B0303';
import RT0401 from 'components/Tables/Reporting/B0401/B0401';
import RT0501 from 'components/Tables/Reporting/B0501/B0501';
import RT0502 from 'components/Tables/Reporting/B0502/B0502';
import RT0601 from 'components/Tables/Reporting/B0601/B0601';
import RT0701 from 'components/Tables/Reporting/B0701/B0701';
import B9901 from 'components/Tables/Reporting/B9901/B9901';
import { sheetTitles, columnHeadersMap } from 'constants/excelData';
import { generateExcelData } from 'utils/hooks/excelUtils';
import useFilters from 'utils/hooks/useFilters';
import { ReportModalProps, TitleKey } from 'store/ducks/Reportings/types';
import AdditionalInformation from 'components/AdditionalInformation/AdditionalInformation';
import { titleDescriptions } from 'constants/reportTitleDescription';
import * as XLSX from 'xlsx';
import * as Styled from '../Tables/DataEntry/DataEntry.styled';

const ReportModal: React.FC<ReportModalProps> = ({
  title,
  tableNumber,
  description,
}) => {
  const { date } = useSelector((state: RootState) => state.reportings);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const queryParams = qs.parse(location.search, { ignoreQueryPrefix: true });
  const entityName =
    typeof queryParams.entity_name === 'string' ? queryParams.entity_name : '';
  const { queryLei } = useFilters();
  const selectedEntityNames = queryLei.entityId?.join(', ') || '';
  const showModal = () => setOpen(true);
  const handleCancel = () => setOpen(false);

  useEffect(() => {
    if (entityName) {
      console.log(entityName, 'entityName');
    }
  }, [entityName]);

  const fetchDataByTableNumber = async (tableNumber: string, params: any) => {
    switch (tableNumber) {
      case 'b_01.03':
        return await reportingOp.fetchBranches(params);
      case 'b_02.03':
        return await reportingOp.fetchEntityContractualArrangements(params);
      case 'b_03.01':
        return await reportingOp.fetchContractualArrangementsLei(params);
      case 'b_03.03':
        return await reportingOp.fetchContractualArrangementsICT(params);
      case 'b_04.01':
        return await reportingOp.fetchContractualArrangementsICTUse(params);
      case 'b_01.01':
        return await reportingOp.fetchRT0101(params);
      case 'b_01.02':
        return await reportingOp.fetchRT0102(params);
      case 'b_02.01':
        return await reportingOp.fetchRT0201(params);
      case 'b_02.02':
        return await reportingOp.fetchRT0202(params);
      case 'b_03.02':
        return await reportingOp.fetchRT0302(params);
      case 'b_05.01':
        return await reportingOp.fetchRT0501(params);
      case 'b_05.02':
        return await reportingOp.fetchRT0502(params);
      case 'b_06.01':
        return await reportingOp.fetchRT0601(params);
      case 'b_07.01':
        return await reportingOp.fetchRT0701(params);
      case 'b_99.01':
        return await reportingOp.fetchB9901(params);
      default:
        throw new Error('Invalid table number');
    }
  };

  const handleOk = async () => {
    setLoading(true);
    try {
      const params = { entityId: queryLei.entityId, date };
      const fetchedData = await fetchDataByTableNumber(tableNumber, params);

      let excelData: any[] = [];

      if (Array.isArray(fetchedData?.filtered_data)) {
        excelData = fetchedData.filtered_data;
      } else if (
        fetchedData?.entities &&
        typeof fetchedData.entities === 'object'
      ) {
        excelData = Object.values(fetchedData.entities).flat();
      } else {
        console.warn('Unknown response shape:', fetchedData);
      }

      if (!excelData.length) {
        console.warn(`No data to export for table ${tableNumber}`);
        return;
      }

      excelData = excelData.map((item: any) => ({
        ...item,
        date: fetchedData?.date,
        entity_name: fetchedData?.entity_name,
      }));

      const { wb, title: excelTitle } = generateExcelData(
        excelData,
        title,
        columnOrderMapReports,
        sheetTitles,
        columnHeadersMap,
      );

      XLSX.writeFile(wb, `${excelTitle}.xlsx`);
    } catch (error) {
      console.log('Failed to fetch data:', error);
    } finally {
      setLoading(false);
      setOpen(false);
    }
  };

  const renderTableComponent = () => {
    const props = { entityName: selectedEntityNames };
    switch (title) {
      case 'b_01.01':
        return <RT0101 {...props} />;
      case 'b_01.02':
        return <RT0102 {...props} />;
      case 'b_01.03':
        return <RT0103 {...props} />;
      case 'b_02.01':
        return <RT0201 {...props} />;
      case 'b_02.02':
        return <RT0202 {...props} />;
      case 'b_02.03':
        return <RT0203 {...props} />;
      case 'b_03.01':
        return <RT0301 {...props} />;
      case 'b_03.02':
        return <RT0302 {...props} />;
      case 'b_03.03':
        return <RT0303 {...props} />;
      case 'b_04.01':
        return <RT0401 {...props} />;
      case 'b_05.01':
        return <RT0501 {...props} />;
      case 'b_05.02':
        return <RT0502 {...props} />;
      case 'b_06.01':
        return <RT0601 {...props} />;
      case 'b_07.01':
        return <RT0701 {...props} />;
      case 'b_99.01':
        return <B9901 {...props} />;
      default:
        return null;
    }
  };

  function isTitleKey(key: string): key is TitleKey {
    return key in titleDescriptions;
  }

  const desc = isTitleKey(title)
    ? titleDescriptions[title]
    : { en: '', de: '' };

  return (
    <>
      <Button type="primary" onClick={showModal}>
        {title}
      </Button>
      <Modal
        open={open}
        centered
        width={1500}
        title={
          <Styled.TitleComponent>
            <div>
              <p>{title}</p>
            </div>
            <AdditionalInformation color="red" description={desc.en} />
            <AdditionalInformation color="#FFCD0B" description={desc.de} />
          </Styled.TitleComponent>
        }
        onOk={handleOk}
        onCancel={handleCancel}
        wrapClassName="reporting-modal"
        footer={[
          <Button key="back" onClick={handleCancel}>
            Return
          </Button>,
          queryLei.entityId &&
            queryLei.entityId.length > 0 &&
            title !== 'b_99.01' && (
              <Button
                key="submit"
                type="primary"
                loading={loading}
                onClick={handleOk}
              >
                <DownloadOutlined /> Download
              </Button>
            ),
        ]}
      >
        {renderTableComponent()}
      </Modal>
    </>
  );
};

export default ReportModal;
